import { PlusOutlined } from '@ant-design/icons';
import { Button, Drawer, DrawerProps } from 'antd';
import { Helmet } from 'react-helmet-async';
import { Trans, useTranslation } from 'react-i18next';

import useAPIMutation from '../../api/useAPIMutation';
import useAPIQuery from '../../api/useAPIQuery';
import useInfiniteAPIQuery from '../../api/useInfiniteAPIQuery';
import usePrefetchPages from '../../hooks/usePrefetchPages';
import { CreateRequestJobPostingTemplateStageSerializer } from '../../types/api';
import Form from '../Form';
import RequestJobPostingTemplateForm, {
  RequestJobPostingTemplateFormValues,
} from './RequestJobPostingTemplateForm';

interface AddRequestJobPostingTemplateProps extends DrawerProps {
  onSuccess?: (
    data: unknown,
    variables: RequestJobPostingTemplateFormValues,
    context: unknown
  ) => void | Promise<unknown>;
}

const AddRequestJobPostingTemplate = ({
  onSuccess,
  ...props
}: AddRequestJobPostingTemplateProps) => {
  const { t } = useTranslation();

  const [form] = Form.useForm();
  const config = useAPIQuery('/configs/tenant-configurations/');

  const usersPages = useInfiniteAPIQuery(
    [
      '/users/',
      {
        params: {
          is_active: true,
          groups__permissions__codename__in: [
            'can_approve_marked_request_job_postings',
            ...(config.data?.t010_enableNewApproverProfile
              ? ['can_approve_all_request_job_postings']
              : []),
          ].join(','),
        },
      },
    ],
    {
      enabled: !config.isLoading,
    }
  );
  const users = usePrefetchPages(usersPages, (data) => data.results);

  const mutation = useAPIMutation(
    async ({ name, templateStages }: RequestJobPostingTemplateFormValues, api) => {
      api.post('/request-job-posting-templates/', {
        name,
        template_stages: templateStages.map((stage, idx) => ({
          name: stage.name,
          sequence: idx,
          approver: stage.approver,
        })) as CreateRequestJobPostingTemplateStageSerializer[],
      });
    },
    {
      onSuccess,
    }
  );

  return (
    <Drawer
      title={t('Adicionar modelo de requisição de vaga')}
      width={720}
      footer={
        <Button
          type="primary"
          htmlType="submit"
          icon={<PlusOutlined />}
          loading={mutation.isLoading}
          onClick={() => form.submit()}
        >
          {' '}
          <Trans>Adicionar</Trans>
        </Button>
      }
      {...props}
    >
      <Helmet>
        <title>
          {t('Adicionar modelo de requisição de vaga')} | {t('Recrutamento e Seleção')}
        </title>
      </Helmet>
      <Form<RequestJobPostingTemplateFormValues>
        form={form}
        layout="vertical"
        onFinish={async (variables) => {
          mutation.mutate(variables);
        }}
        initialValues={{
          name: '',
          templateStages: [],
        }}
      >
        <RequestJobPostingTemplateForm users={users} />
      </Form>
    </Drawer>
  );
};

export default AddRequestJobPostingTemplate;
